import React, { useContext, useEffect, useState } from 'react';
import UserContext from '../UserContext';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControlLabel,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { getFirmPendingInvites, getFirmUsers, inviteUserToFirm, removeUserFromFirm, updateFirm, updateFirmAccess } from '../rest/BackendApi';
import { FirmUser, Invite, InviteResult } from '../types/FirmType';
import { visuallyHidden } from '@mui/utils';
import SendIcon from '@mui/icons-material/Send';
import EditIcon from '@mui/icons-material/Edit';
import Loading, { SimpleLoading } from '../components/Loading';
import { getFeatureLimit, isUnlimited } from '../business/Subscription';
import { displayDate } from '../util/TimeUtil';
import ConfirmDialog from '../components/ConfirmDialog';
import MessageContext, { empty, error, success } from '../MessageContext';
import { DetailRow, PageContainer, PageHeader, Section } from '../components/Page';
import { plural } from '../util/StringUtil';

type ConfirmType = {
  open: boolean;
  title?: string;
  message?: React.ReactNode;
  confirmText?: string;
  onConfirm?: Function;
};

const INVITE_MESSAGES: Record<InviteResult, { ok: boolean; text: string }> = {
  ALREADY_IN_FIRM: { ok: false, text: 'That user is already part of the firm.' },
  ALREADY_IN_ANOTHER_FIRM: { ok: false, text: 'That user is already part of another firm. Please contact them.' },
  ALREADY_INVITED: { ok: false, text: "That user has already been invited but hasn't accepted yet." },
  INVITE_EMAIL_SENT: { ok: true, text: "Invite sent. They'll join the firm once they accept it." },
  USER_LIMIT_REACHED: { ok: false, text: 'Your plan has no more users available. Remove a user or upgrade your subscription to invite someone new.' },
  ERROR: { ok: false, text: "We couldn't invite the user to this firm. Please contact support." },
};

const headCellSx = { backgroundColor: '#f5f5f5', fontWeight: 600, py: 1 };

export default function FirmPage() {
  const userContext = useContext(UserContext);
  const messageContext = useContext(MessageContext);
  const user = userContext?.user;
  const [firmUsers, setFirmUsers] = useState<FirmUser[]>([]);
  const [pendingInvites, setPendingInvites] = useState<Invite[]>([]);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [inviteEmail, setInviteEmail] = useState<string>('');
  const [firmName, setFirmName] = useState<string>('');
  const [initialised, setInitialised] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmType>({ open: false });

  const fetchPendingInvites = async () => {
    if (user?.firm) {
      try {
        setPendingInvites(await getFirmPendingInvites(user.firm.id));
      } catch (e) {
        console.error(e);
      }
    }
  };

  const fetchAllUsers = async () => {
    if (user?.firm) {
      try {
        setFirmUsers(await getFirmUsers(user.firm.id));
        await fetchPendingInvites();
      } catch (e) {
        messageContext.setMessage(error('Failed to load the users for that firm. Make sure you are the billing admin'));
      }
    }
    setInitialised(true);
  };

  useEffect(() => {
    if (user?.firm?.name) {
      setFirmName(user.firm.name);
    }
    fetchAllUsers();
  }, [user]);

  const withBusy = async (action: () => Promise<void>) => {
    setBusy(true);
    try {
      await action();
    } finally {
      setBusy(false);
    }
  };

  const handleNameChange = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!user?.firm || !firmName.trim()) return;
    await withBusy(async () => {
      try {
        await updateFirm(user.firm!.id, firmName.trim());
        await userContext.refreshUser();
        messageContext.setMessage(success('Firm name updated', { icon: 'firm' }));
        setEditMode(false);
      } catch (exception) {
        messageContext.setMessage(error('Failed to update firm name'));
      }
    });
  };

  const cancelNameChange = () => {
    setFirmName(user?.firm?.name || '');
    setEditMode(false);
  };

  const saveAccess = async (checked: boolean) => {
    if (!user?.firm) return;
    await withBusy(async () => {
      try {
        await updateFirmAccess(user.firm!.id, checked);
        await userContext.refreshUser();
        messageContext.setMessage(success(checked ? 'Open access turned on' : 'Open access turned off', { icon: checked ? 'lockOpen' : 'lock' }));
      } catch (exception) {
        messageContext.setMessage(error('Failed to update firm access'));
      }
    });
  };

  const handleAccessChange = (checked: boolean) => {
    if (checked) {
      // Turning this on exposes Xero data to the whole firm, so ask first.
      setConfirm({
        open: true,
        title: 'Turn on open access?',
        message:
          'Every user in your firm will be able to see the organisations connected by other users, including Xero data they may not have access to in Xero.',
        confirmText: 'Turn on',
        onConfirm: () => saveAccess(true),
      });
    } else {
      saveAccess(false);
    }
  };

  const handleInvite = async (event: React.FormEvent) => {
    event.preventDefault();
    messageContext.setMessage(empty());
    if (inviteEmail.trim() === '' || !user?.firm) return;
    await withBusy(async () => {
      try {
        const { result } = await inviteUserToFirm(user.firm!.id, inviteEmail.trim());
        const outcome = INVITE_MESSAGES[result as InviteResult] || INVITE_MESSAGES.ERROR;
        messageContext.setMessage(outcome.ok ? success(outcome.text, { icon: 'personAdd' }) : error(outcome.text));
        if (result === 'INVITE_EMAIL_SENT') await fetchPendingInvites();
        if (outcome.ok) setInviteEmail('');
      } catch (e) {
        messageContext.setMessage(error('Failed to invite user.'));
      }
    });
  };

  const askRemove = (firmUser: FirmUser) => {
    setConfirm({
      open: true,
      title: 'Remove user?',
      message: `${firmUser.givenName} ${firmUser.familyName} will lose access to this firm's organisations in BAS Ready.`,
      confirmText: 'Remove',
      onConfirm: async () => {
        if (!user?.firm) return;
        await withBusy(async () => {
          try {
            await removeUserFromFirm(user.firm!.id, firmUser.id);
            await fetchAllUsers();
            messageContext.setMessage(success('User removed from firm', { icon: 'personRemove' }));
          } catch (exception) {
            messageContext.setMessage(error('Failed to remove user from firm'));
          }
        });
      },
    });
  };

  if (!user || !user.firm) {
    return null;
  }

  const userLimit = getFeatureLimit(user, 'MultiUser');
  const usersAtLimit = firmUsers.length >= userLimit;

  return (
    <PageContainer>
      {busy && <Loading />}
      <ConfirmDialog
        title={confirm.title || 'Confirmation'}
        open={confirm.open}
        onConfirm={() => confirm.onConfirm && confirm.onConfirm()}
        setOpen={(open: boolean) => setConfirm({ ...confirm, open })}
        cancelText="Cancel"
        confirmText={confirm.confirmText || 'Confirm'}
      >
        {confirm.message}
      </ConfirmDialog>

      <PageHeader title="Manage Firm" description="Your firm's name, who can use BAS Ready, and what they can see." />

      <Section
        title="Firm details"
        actions={
          !editMode && (
            <Button size="small" variant="outlined" startIcon={<EditIcon fontSize="small" />} onClick={() => setEditMode(true)}>
              Rename
            </Button>
          )
        }
      >
        {editMode ? (
          <Box component="form" onSubmit={handleNameChange}>
            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} gap={1.5}>
              <TextField
                autoFocus
                size="small"
                sx={{ flex: 1, maxWidth: 480 }}
                value={firmName}
                required
                onChange={e => setFirmName(e.target.value)}
                label="Firm name"
              />
              <Stack direction="row" gap={1}>
                <Button type="submit" variant="contained" disabled={!firmName.trim() || firmName.trim() === user.firm.name}>
                  Save
                </Button>
                <Button onClick={cancelNameChange} color="inherit">
                  Cancel
                </Button>
              </Stack>
            </Stack>
          </Box>
        ) : (
          <DetailRow label="Firm name" value={<b>{user.firm.name}</b>} />
        )}
      </Section>

      <Section
        title="Users"
        description={
          <>
            {isUnlimited(userLimit) ? plural(firmUsers.length, 'user') : `${firmUsers.length} of ${userLimit} users on your plan`}
            {pendingInvites.length > 0 && ` · ${plural(pendingInvites.length, 'invite')} pending`}
          </>
        }
      >
        {!initialised ? (
          <SimpleLoading message="Loading users" />
        ) : (
          <>
            {usersAtLimit ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                You've reached the {userLimit} users included in your plan. Remove a user or upgrade your subscription to invite more.
              </Alert>
            ) : (
              <Box component="form" onSubmit={handleInvite} sx={{ mb: 2 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5} alignItems={{ sm: 'center' }}>
                  <TextField
                    size="small"
                    value={inviteEmail}
                    required
                    onChange={e => setInviteEmail(e.target.value)}
                    label="Invite by email"
                    placeholder="name@yourfirm.com.au"
                    type="email"
                    sx={{ flex: 1, maxWidth: 400 }}
                  />
                  <Button variant="contained" type="submit" endIcon={<SendIcon />} disabled={!inviteEmail.trim()}>
                    Send invite
                  </Button>
                </Stack>
              </Box>
            )}

            <TableContainer sx={{ border: '1px solid #eee', borderRadius: 1 }}>
              <Table size="small" aria-label="Firm users">
                <TableHead>
                  <TableRow>
                    <TableCell sx={headCellSx}>Name</TableCell>
                    <TableCell sx={headCellSx}>Email</TableCell>
                    <TableCell sx={headCellSx}>Role</TableCell>
                    <TableCell sx={headCellSx} align="right">
                      <Box component="span" sx={visuallyHidden}>
                        Actions
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {firmUsers.map(firmUser => (
                    <TableRow key={`user${firmUser.id}`} hover>
                      <TableCell>
                        {firmUser.givenName} {firmUser.familyName}
                        {firmUser.id === user.id && (
                          <Typography component="span" variant="body2" color="text.secondary">
                            {' '}
                            (you)
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{firmUser.email}</TableCell>
                      <TableCell>
                        {firmUser.isBillingAdmin ? (
                          <Chip size="small" label="Billing admin" color="primary" variant="outlined" />
                        ) : (
                          <Chip size="small" label="Member" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {!firmUser.isBillingAdmin && (
                          <Button size="small" color="error" onClick={() => askRemove(firmUser)}>
                            Remove
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {pendingInvites.map(invite => (
                    <TableRow key={`invite${invite.id}`}>
                      <TableCell sx={{ color: 'text.secondary', fontStyle: 'italic' }}>Invite not accepted yet</TableCell>
                      <TableCell>{invite.email}</TableCell>
                      <TableCell>
                        <Chip size="small" label={`Invited ${displayDate(invite.creationDate)}`} color="warning" variant="outlined" />
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {pendingInvites.length > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                Invited users get an email with a link to sign up. If it doesn't arrive, send them <b>{window.location.host}/signup</b> and ask them to use
                “Sign up with Xero” with the invited email. They join your firm once they accept the invite.
              </Typography>
            )}
          </>
        )}
      </Section>

      <Section title="Organisation access" description="Choose whether users can see organisations connected by other people in the firm.">
        <FormControlLabel
          control={<Switch checked={user.firm.openAccess} onChange={(_, checked) => handleAccessChange(checked)} />}
          label={
            <Box>
              <Typography sx={{ fontWeight: 600 }}>Open access {user.firm.openAccess ? 'on' : 'off'}</Typography>
              <Typography variant="body2" color="text.secondary">
                {user.firm.openAccess
                  ? 'Everyone in the firm can see every connected organisation, even ones they can’t access in Xero.'
                  : 'Users only see the organisations they connected themselves.'}
              </Typography>
            </Box>
          }
          sx={{ alignItems: 'flex-start', '.MuiSwitch-root': { mt: -0.5 } }}
        />
      </Section>
    </PageContainer>
  );
}
